import usePageMetadata, { PageMetadata } from '@hooks/usePageMetadata'
import { CookWidget } from '@widgets/types'
import { FunctionComponent, useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import styled, { css } from 'styled-components'
import { ToplistAPIProps, StoryAPIProps } from './types'
import { getToplist, isValidToplistStories } from './utils'
import { desktopCSS } from '@measures/responsive'
import Story from './story'
import translate from '@i18n'

const StyledWrapper = styled.div`
  ${({
    theme: {
      colors: {
        grey: { grey_20 },
      },
      fonts: { goodOT },
    },
  }) => {
    return css`
      font-family: ${goodOT};
      width: 100%;
      background-color: ${grey_20};
      padding-bottom: 4px;
    `
  }}
`

const Title = styled.div`
  ${({
    theme: {
      fonts: { goodOT },
      colors: {
        grey: { grey_20, grey_100 },
      },
    },
  }) => {
    return css`
      font-family: ${goodOT};
      font-size: 17px;
      font-weight: 900;
      line-height: 20px;
      background-color: ${grey_20};
      color: ${grey_100};
      text-align: center;
      padding: 2px 0 1px 0;
      text-transform: uppercase;
      ${desktopCSS(css`
        text-align: left;
        padding: 2px 0 1px 18px;
      `)}
    `
  }}
`

interface ChildrenContainerProps {
  isHomeOrTestSection: boolean
}

const ChildrenContainer = styled.ol<ChildrenContainerProps>`
  margin: 0;
  display: grid;
  padding: 15px 0 15px 18px;
  box-sizing: border-box;
  width: 100%;
  background-color: #fff;
  grid-template-rows: repeat(3, minmax(0, 1fr));
  grid-row-gap: 30px;
  ${({ isHomeOrTestSection }) => {
    // Widget toplist is shown with "horizontal" layout only in the home page and in the Test Section
    if (isHomeOrTestSection) {
      return css`
        ${desktopCSS(css`
          grid-row-gap: 0;
          grid-column-gap: 52px;
          grid-template-rows: minmax(0, 1fr);
          grid-template-columns: repeat(3, minmax(0, 1fr));
        `)}
      `
    }
  }}
`

const Toplist: FunctionComponent<ToplistAPIProps> = (props) => {
  const pageMetadata = usePageMetadata()

  const [stories, setStories] = useState<StoryAPIProps[]>(() =>
    isValidToplistStories(props.stories)
      ? props.stories
          .filter((story: any) => Object.keys(story).length > 0)
          .slice(0, 3)
      : []
  )

  const id = (pageMetadata as PageMetadata)?.id

  const { data: apiToplistData } = useQuery<StoryAPIProps[]>(
    ['stories', id],
    () => getToplist(id as string),
    {
      enabled: !!id && stories.length > 2,
    }
  )

  useEffect(() => {
    if (isValidToplistStories(apiToplistData)) {
      setStories(apiToplistData)
    }
  }, [apiToplistData])

  const title = props.title || translate('topList.title')

  if (!(stories?.length > 0)) {
    return null
  }

  return (
    <StyledWrapper>
      <Title>{title}</Title>
      <ChildrenContainer
        isHomeOrTestSection={
          pageMetadata.context === 'home' ||
          pageMetadata.contextId === '/Test_Section/'
        }>
        {stories.map((story, index) => {
          const { articleId, title, catchword, link } = story
          return (
            <Story
              key={articleId}
              order={index + 1}
              title={title}
              catchword={catchword}
              articleId={articleId}
              link={link}
            />
          )
        })}
      </ChildrenContainer>
    </StyledWrapper>
  )
}

const widget: CookWidget = {
  kind: ['widget', 'toplist'],
  component: Toplist,
}

export default widget
